/**
 * TYPES
 */
import {_aggregates} from 'src/aggregates/index.d';
import {_names} from 'src/aggregates/preferences/commands.d';
import {IPreferencesAddAutoMessage} from 'src/aggregates/preferences/commands.d';
import {IPreferencesDisableTour} from 'src/aggregates/preferences/commands.d';
import {IPreferencesLoad} from 'src/aggregates/preferences/commands.d';
import {IPreferencesRemoveAutoMessage} from 'src/aggregates/preferences/commands.d';
import {IPreferencesSetCurrentAutoMessage} from 'src/aggregates/preferences/commands.d';
import {IPreferencesSetEmailPageSize} from 'src/aggregates/preferences/commands.d';
import {IPreferencesSetFeedback} from 'src/aggregates/preferences/commands.d';
import {IPreferencesSetTagColor} from 'src/aggregates/preferences/commands.d';
import {IPreferencesStart} from 'src/aggregates/preferences/commands.d';
import {IPreferencesSwitchMuteNotifications} from 'src/aggregates/preferences/commands.d';
import {IPreferencesSwitchPaused} from 'src/aggregates/preferences/commands.d';
import {IPreferencesSwitchWalletReminder} from 'src/aggregates/preferences/commands.d';


/**
 * CODE
 */

/**
 * I am a command creator for preferences add auto message.
 *
 * :param message: the message text to add
 * :param user: logged user id
 *
 * :returns: preferences add auto message command
 */
function addAutoMessage(
    message: string,
    user: number,
): IPreferencesAddAutoMessage
{
    return {
        _aggregate: _aggregates.PREFERENCES,
        _name: _names.PREFERENCES_ADD_AUTO_MESSAGE,
        _target: user,
        message: message,
    };
}


/**
 * I am a command creator for preferences disable tour.
 *
 * :param user: logged user id
 *
 * :returns: preferences disable tour command
 */
function disableTour(user: number): IPreferencesDisableTour
{
    return {
        _aggregate: _aggregates.PREFERENCES,
        _name: _names.PREFERENCES_DISABLE_TOUR,
        _target: user,
    };
}


/**
 * I am a command creator for preferences load.
 *
 * :param user: logged user id
 *
 * :returns: preferences load command
 */
function load(user: number): IPreferencesLoad
{
    return {
        _aggregate: _aggregates.PREFERENCES,
        _name: _names.PREFERENCES_LOAD,
        _target: user,
    };
}


/**
 * I am a command creator for preferences remove auto message.
 *
 * :param message: the message id to remove
 * :param user: logged user id
 *
 * :returns: preferences remove auto message command
 */
function removeAutoMessage(
    message: number,
    user: number,
): IPreferencesRemoveAutoMessage
{
    return {
        _aggregate: _aggregates.PREFERENCES,
        _name: _names.PREFERENCES_ADD_AUTO_MESSAGE,
        _target: user,
        message,
    };
}


/**
 * I am a command creator for preferences set current auto message.
 *
 * :param message: the message id to use
 * :param user: logged user id
 *
 * :returns: preferences set current auto message command
 */
function setCurrentAutoMessage(
    message: number,
    user: number,
): IPreferencesSetCurrentAutoMessage
{
    return {
        _aggregate: _aggregates.PREFERENCES,
        _name: _names.PREFERENCES_SET_CURRENT_AUTO_MESSAGE,
        _target: user,
        message,
    };
}


/**
 * I am a command creator for preferences set email page size.
 *
 * :param size: the size of email page
 * :param user: logged user id
 *
 * :returns: preferences set email page size command
 */
function setEmailPageSize(
    size: number,
    user: number,
): IPreferencesSetEmailPageSize
{
    return {
        _aggregate: _aggregates.PREFERENCES,
        _name: _names.PREFERENCES_SET_EMAIL_PAGE_SIZE,
        _target: user,
        size,
    };
}


/**
 * I am a command creator for set preferences feedback.
 *
 * :param feedback: last feedback's timestamp
 * :param user: logged user id
 *
 * :returns: preferences feedback command
 */
function setFeedback(
    feedback: number,
    user: number,
): IPreferencesSetFeedback
{
    return {
        _aggregate: _aggregates.PREFERENCES,
        _name: _names.PREFERENCES_SET_FEEDBACK,
        _target: user,
        feedback,
    };
}


/**
 * I am a command creator for preferences update tag color.
 *
 * :returns: preferences start command
 */
function setTagColor(
    color: string,
    tag: number,
    user: number,
): IPreferencesSetTagColor
{
    return {
        _aggregate: _aggregates.PREFERENCES,
        _name: _names.PREFERENCES_SET_TAG_COLOR,
        _target: user,
        color,
        tag,
    };
}


/**
 * I am a command creator for preferences start.
 *
 * :param user: logged user id
 *
 * :returns: preferences start command
 */
function start(user: number): IPreferencesStart
{
    return {
        _aggregate: _aggregates.PREFERENCES,
        _name: _names.PREFERENCES_START,
        _target: user,
    };
}


/**
 * I am a command creator for preferences switch mute notifications.
 *
 * :param muteNotifications: mute notifications state
 *
 * :returns: preferences switch mute notifications command
 */
function switchMuteNotifications(
    user: number,
    muteNotifications: boolean,
): IPreferencesSwitchMuteNotifications
{
    return {
        _aggregate: _aggregates.PREFERENCES,
        _name: _names.PREFERENCES_SWITCH_MUTE_NOTIFICATIONS,
        _target: user,
        muteNotifications,
    };
}


/**
 * I am a command creator for preferences switch paused.
 *
 * :param paused: paused state
 *
 * :returns: preferences switch paused command
 */
 function switchPaused(
    user: number,
    paused: boolean,
): IPreferencesSwitchPaused
{
    return {
        _aggregate: _aggregates.PREFERENCES,
        _name: _names.PREFERENCES_SWITCH_PAUSED,
        _target: user,
        paused,
    };
}


/**
 * I am a command creator for preferences switch wallet reminder.
 *
 * :param user: logged user id
 * :param walletReminder: wallet reminder display state
 *
 * :returns: preferences switch wallet reminder command
 */
function switchWalletReminder(
    user: number,
    walletReminder: boolean,
): IPreferencesSwitchWalletReminder
{
    return {
        _aggregate: _aggregates.PREFERENCES,
        _name: _names.PREFERENCES_SWITCH_WALLET_REMINDER,
        _target: user,
        walletReminder,
    };
}


/**
 * EXPORTS
 */
export {
    addAutoMessage,
    disableTour,
    load,
    removeAutoMessage,
    setCurrentAutoMessage,
    setEmailPageSize,
    setFeedback,
    setTagColor,
    start,
    switchMuteNotifications,
    switchPaused,
    switchWalletReminder,
};
